/**
 * useStreakSystem - Hook for managing player streaks
 * 
 * Now uses PlayerBootstrapStore for initial load to reduce duplicate requests.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { debugProgress } from '@/lib/debug';
import { useCharacterStore } from './useCharacterStore';
import { useChallengeSystem } from './useChallengeSystem';
import { useWallet } from './useWallet';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentPlayer } from './useCurrentPlayer';
import { usePlayerAuth } from './usePlayerAuth';
import { SessionManager } from '@/services/sessionManager';
import { 
  loadPlayerBootstrap, 
  getCachedPlayerBootstrap,
  updateCachedStreaks,
  type StreakData as BootstrapStreakData
} from '@/stores/playerBootstrapStore';

interface StreakData {
  currentStreak: number;
  bestStreak: number;
  totalCorrectAnswers: number;
  lastAnswerTime: string | null;
  streakMultiplier: number;
}

interface StreakReward {
  type: 'money' | 'experience';
  amount: number;
  message: string;
}

const STREAK_TIMEOUT_HOURS = 2; // Streak breaks after 2 hours of inactivity

// Streak milestones and their rewards
const STREAK_REWARDS: Record<number, StreakReward> = {
  5: { type: 'money', amount: 50, message: '5 oikeaa putkeen! Bonus: 50€' },
  10: { type: 'money', amount: 100, message: '10 oikeaa putkeen! Bonus: 100€' },
  15: { type: 'money', amount: 200, message: '15 oikeaa putkeen! Bonus: 200€' },
  20: { type: 'money', amount: 300, message: '20 oikeaa putkeen! Bonus: 300€' },
  25: { type: 'money', amount: 500, message: '25 oikeaa putkeen! Bonus: 500€' },
};

export const useStreakSystem = () => {
  const [streakData, setStreakData] = useState<StreakData>({
    currentStreak: 0,
    bestStreak: 0,
    totalCorrectAnswers: 0,
    lastAnswerTime: null,
    streakMultiplier: 1.0,
  });
  const [loading, setLoading] = useState(true);
  
  // Prevent double loads
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);

  // Toast removed - success notifications disabled per notification policy
  const { addPoints } = useCharacterStore();
  const { earnMoney } = useWallet();
  const { updateProgress } = useChallengeSystem();
  const { currentPlayer } = useCurrentPlayer();
  const { playerSession } = usePlayerAuth();

  // Load streak data using bootstrap store
  useEffect(() => {
    mountedRef.current = true;
    
    if (!currentPlayer?.id || !playerSession?.sessionToken) {
      setLoading(false);
      return;
    }
    
    if (loadingRef.current) return;

    const loadStreakData = async () => {
      loadingRef.current = true;
      
      try {
        // Try bootstrap cache first
        let streaks = getCachedPlayerBootstrap(currentPlayer.id)?.streaks;
        
        if (!streaks) {
          // Load via bootstrap store
          const bootstrap = await loadPlayerBootstrap({
            playerId: currentPlayer.id,
            sessionToken: playerSession.sessionToken,
            reason: 'useStreakSystem'
          });
          streaks = bootstrap.streaks;
        }
        
        if (!mountedRef.current) return;

        if (streaks) {
          setStreakData({
            currentStreak: streaks.current_streak,
            bestStreak: streaks.best_streak,
            totalCorrectAnswers: streaks.total_correct_answers,
            lastAnswerTime: streaks.last_answer_time,
            streakMultiplier: streaks.streak_multiplier,
          });
        }
      } catch (error) {
        console.error('Error loading streak data:', error);
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
        loadingRef.current = false;
      }
    };

    loadStreakData();
    
    return () => {
      mountedRef.current = false;
    };
  }, [currentPlayer?.id, playerSession?.sessionToken]);

  const calculateStreakMultiplier = (streak: number): number => {
    if (streak >= 20) return 3.0;
    if (streak >= 15) return 2.5;
    if (streak >= 10) return 2.0;
    if (streak >= 5) return 1.5;
    return 1.0;
  };

  const recordCorrectAnswer = async (questionId: string, moduleId: string, basePoints: number) => {
    if (!currentPlayer?.id || !playerSession?.sessionToken) return;

    const now = new Date().toISOString();
    
    const newStreak = streakData.currentStreak + 1;
    const newBestStreak = Math.max(newStreak, streakData.bestStreak);
    const newMultiplier = calculateStreakMultiplier(newStreak);
    
    // Calculate bonus points from streak
    const bonusPoints = Math.floor(basePoints * (newMultiplier - 1));
    const totalPoints = basePoints + bonusPoints;
    
    // Update local state immediately for UI responsiveness
    const newStreakData = {
      currentStreak: newStreak,
      bestStreak: newBestStreak,
      totalCorrectAnswers: streakData.totalCorrectAnswers + 1,
      lastAnswerTime: now,
      streakMultiplier: newMultiplier,
    };
    setStreakData(newStreakData);
    
    // Update bootstrap cache
    updateCachedStreaks(currentPlayer.id, {
      current_streak: newStreak,
      best_streak: newBestStreak,
      total_correct_answers: streakData.totalCorrectAnswers + 1,
      last_answer_time: now,
      streak_multiplier: newMultiplier
    });

    // Update challenge system with correct answer and points
    updateProgress('correct_answer', totalPoints);
    updateProgress('streak_achieved', newStreak);
    
    // Add bonus points for streak (points determine level progression)
    if (bonusPoints > 0) {
      addPoints(bonusPoints);
    }

    // Check for streak milestone rewards (money rewards for special milestones)
    const reward = STREAK_REWARDS[newStreak];
    if (reward) {
      if (reward.type === 'money') {
        // Use wallet for money - goes to player_financial_status
        // Fire-and-forget is acceptable for streak rewards, but log any errors
        earnMoney(reward.amount).catch(err => console.warn('Streak reward earnMoney failed:', err));
      } else {
        addPoints(reward.amount); // Points instead of experience
      }
      
      // Log streak reward (no toast per notification policy)
      debugProgress(`Streak reward: ${reward.message}`, { streak: newStreak, reward });
    } else if (bonusPoints > 0) {
      // Log streak bonus (no toast per notification policy)
      debugProgress(`Streak bonus: +${bonusPoints} points (${newMultiplier}x)`, { streak: newStreak, bonusPoints });
    }

    // Persist to database via session-validated RPC with retry logic
    try {
      await SessionManager.withSessionRetry(async (session) => {
        const { data, error } = await supabase.rpc('save_player_streak_with_session', {
          p_session_token: session.sessionToken,
          p_current_streak: newStreak,
          p_best_streak: newBestStreak,
          p_total_correct_answers: streakData.totalCorrectAnswers + 1,
          p_streak_multiplier: newMultiplier
        });

        if (error) {
          console.error('Error saving streak data via RPC:', error);
        } else if (data) {
          const result = data as { success: boolean; reason?: string; error?: string };
          if (!result.success) {
            console.error('Streak RPC failed:', result.reason, result.error);
          }
        }
        return { data, error };
      });
    } catch (error) {
      console.error('Error saving streak data:', error);
    }

    console.log(`Streak recorded: ${questionId} in module ${moduleId}, base points: ${basePoints}`);
  };

  const recordIncorrectAnswer = async () => {
    if (!currentPlayer?.id || !playerSession?.sessionToken) return;

    if (streakData.currentStreak > 0) {
      // Log streak broken (no toast per notification policy)
      debugProgress(`Streak broken at ${streakData.currentStreak}`, { streak: streakData.currentStreak });
    }

    const now = new Date().toISOString();

    // Update local state immediately
    setStreakData(prev => ({
      ...prev,
      currentStreak: 0,
      streakMultiplier: 1.0,
      lastAnswerTime: now,
    }));
    
    // Update bootstrap cache
    updateCachedStreaks(currentPlayer.id, {
      current_streak: 0,
      best_streak: streakData.bestStreak,
      total_correct_answers: streakData.totalCorrectAnswers,
      last_answer_time: now,
      streak_multiplier: 1.0
    });

    // Persist to database via session-validated RPC with retry logic
    try {
      await SessionManager.withSessionRetry(async (session) => {
        const { data, error } = await supabase.rpc('save_player_streak_with_session', {
          p_session_token: session.sessionToken,
          p_current_streak: 0,
          p_best_streak: streakData.bestStreak,
          p_total_correct_answers: streakData.totalCorrectAnswers,
          p_streak_multiplier: 1.0
        });

        if (error) {
          console.error('Error saving streak reset via RPC:', error);
        } else if (data) {
          const result = data as { success: boolean; reason?: string; error?: string };
          if (!result.success) {
            console.error('Streak reset RPC failed:', result.reason, result.error);
          }
        }
        return { data, error };
      });
    } catch (error) {
      console.error('Error saving streak reset:', error);
    }
  };

  const getStreakStatus = () => {
    const nextMilestone = Object.keys(STREAK_REWARDS)
      .map(Number)
      .find(milestone => milestone > streakData.currentStreak);
    
    return {
      ...streakData,
      nextMilestone,
      progressToNext: nextMilestone ? streakData.currentStreak / nextMilestone : 1,
    };
  };

  return {
    streakData: getStreakStatus(),
    recordCorrectAnswer,
    recordIncorrectAnswer,
    isStreakActive: streakData.currentStreak > 0,
    loading,
  };
};
